import React, { useState, useEffect, Component } from "react";
import * as actions from "./store/actions/auth";
import { connect } from "react-redux";

import logo from "./logo.svg";
import "./App.css";
import MainDisplay from "./main/MainDisplay";
import axios from "axios";
import { API_get_member_stores, API_change_home_store } from "./constants";
import Login from "./Login";
import LoadSpinner from "./utilities/LoadSpinner";
import { getDefaultAxiosConfig } from "./utilities/requests";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import StreamChatWrapper from "./StreamChatWrapper";
import WelcomeAccept from "./welcome/WelcomeAccept";
import ToastCredentialConnect from "./toast_connect/ToastCredentialConnect";
import PublicSwipeDeckView from "./public_swipe_deck/PublicSwipeDeckView";
import PublicFormView from "./public_form/PublicFormView";
import AssetStickerPage from "./helpers/AssetStickerPage";

let axiosConfig = getDefaultAxiosConfig();

function App(props) {
  const [storeOptions, setStoreOptions] = React.useState([]);
  const [homeStore, setHomeStore] = React.useState();
  const [homeCompany, setHomeCompany] = React.useState();
  const [storeNames, setStoreNames] = React.useState();
  const [isLoading, setIsLoading] = React.useState(true);
  const [permissionRole, setPermissionRole] = React.useState();
  const [currentUserId, setCurrentUserId] = React.useState(null);
  const [permissionOverrides, setPermissionOverrides] = React.useState([]);
  const [internalBillingOverviewAccess, setInternalBillingOverviewAccess] = React.useState();
  const [setupAssistantEnabled, setSetupAssistantEnabled] = React.useState(false);
  const [storeInfoList, setStoreInfoList] = React.useState();

  useEffect(() => {
    props.onTryAutoSignup();
  }, []);

  // Check if this is a password reset URL (format: /{uid}/{token})
  const url = window.location.href;
  const pathname = window.location.pathname;
  const urlParts = pathname.split("/").filter(part => part); // Remove empty strings

  // Welcome invite URL: /welcome/<token>
  const isWelcomeAccept =
    urlParts.length === 2 &&
    urlParts[0] === "welcome" &&
    urlParts[1].length > 0;
  const welcomeToken = isWelcomeAccept ? urlParts[1] : null;

  // Toast credential collection URL: /toast-connect/<token>
  const isToastCredentialConnect =
    urlParts.length === 2 &&
    urlParts[0] === "toast-connect" &&
    urlParts[1].length > 0;
  const toastCredentialToken = isToastCredentialConnect ? urlParts[1] : null;

  // Public, unauthenticated swipe deck share URL: /s/<token>
  const isPublicSwipeDeck =
    urlParts.length === 2 &&
    urlParts[0] === "s" &&
    urlParts[1].length > 0;
  const publicSwipeDeckToken = isPublicSwipeDeck ? urlParts[1] : null;

  // Public, unauthenticated form share URL: /f/<token>
  const isPublicForm =
    urlParts.length === 2 &&
    urlParts[0] === "f" &&
    urlParts[1].length > 0;
  const publicFormToken = isPublicForm ? urlParts[1] : null;

  // Maintenance asset sticker URL: /asset/<token>. The mobile app claims
  // this path; anyone it reaches in a browser gets a static how-to page.
  const isAssetSticker =
    urlParts.length === 2 &&
    urlParts[0] === "asset" &&
    urlParts[1].length > 0;

  // Password reset URL has exactly 2 path segments (uid and token) and no query params
  const isPasswordReset =
    !isWelcomeAccept &&
    !isToastCredentialConnect &&
    !isPublicSwipeDeck &&
    !isPublicForm &&
    !isAssetSticker &&
    urlParts.length === 2 &&
    !window.location.search && // No query parameters
    urlParts[0].length > 0 &&
    urlParts[1].length > 0;

  const uid = isPasswordReset ? urlParts[0] : null;
  const token = isPasswordReset ? urlParts[1] : null;

  function get_permission_role(store_id, store_list) {
    let membership = store_list.filter((v, i) => v.store === store_id);
    if (membership.length > 0) {
      return membership[0]["permission_type"]; // ["company_employee","company_manager", "company_admin", "rightwork_internal"];
    } else {
      return "company_employee";
    }
  }

  function get_permission_overrides(store_id, store_list) {
    let membership = store_list.filter((v, i) => v.store === store_id);
    if (membership.length > 0) {
      return membership[0]["permission_type_overrides"] || []; // ["logbook_access", "manager_notifications"];
    } else {
      return [];
    }
  }

  function get_member_stores(username, options = {}) {
    const { showLoading = true } = options;
    if (showLoading) {
      setIsLoading(true);
    }
    if (!props.isAuthenticated) {
      if (showLoading) {
        setIsLoading(false);
      }
      return Promise.resolve();
    }

    axiosConfig = getDefaultAxiosConfig();
    return axios
      .get(API_get_member_stores + username, axiosConfig)
      .then((res) => {
        const store_id = res.data["user_info"]["home_store"];
        setCurrentUserId(res.data["user_info"]["user_id"] ?? null);
        const company_id = res.data["user_info"]["home_company"];
        // console.log("STORE INFO", res.data["membership_info"]);
        setStoreOptions(res.data["membership_info"]);
        setHomeStore(store_id);
        setHomeCompany(company_id);

        setStoreNames(res.data["store_info"]);
        setInternalBillingOverviewAccess(
          Boolean(res.data["user_info"]["internal_billing_overview_access"])
        );
        setSetupAssistantEnabled(
          Boolean(res.data["user_info"]["setup_assistant_enabled"])
        );
        setPermissionRole(
          get_permission_role(store_id, res.data["membership_info"])
        );
        setPermissionOverrides(
          get_permission_overrides(store_id, res.data["membership_info"])
        );
        setStoreInfoList(res.data["store_info"]);
        if (showLoading) {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        alert("No longer a member of any locations, logging out.");
        props.logout();
        if (showLoading) {
          setIsLoading(false);
        }
      });
  }

  function change_home_store(new_home_store) {
    return axios
      .put(
        API_change_home_store + props.username + "&new_store=" + new_home_store,
        {},
        axiosConfig
      )
      .then(() => {
        const storeAlreadyLoaded = storeNames?.some(
          (store) => store.id === new_home_store
        );
        const membershipAlreadyLoaded = storeOptions?.some(
          (membership) => membership.store === new_home_store
        );

        if (storeAlreadyLoaded && membershipAlreadyLoaded) {
          setHomeStore(new_home_store);
          setPermissionRole(get_permission_role(new_home_store, storeOptions));
          setPermissionOverrides(
            get_permission_overrides(new_home_store, storeOptions)
          );
        }

        return get_member_stores(props.username, { showLoading: false });
      })
      .catch((error) => {
        console.error("Error changing home store:", error);
        throw error;
      });
  }

  useEffect(() => {
    get_member_stores(props.username);
  }, [props.isAuthenticated]);

  useEffect(() => {
    if (permissionRole) {
      localStorage.setItem("permissionRole", permissionRole);
      return;
    }

    localStorage.removeItem("permissionRole");
  }, [permissionRole]);

  //// console.log(storeOptions);
  //// console.log(homeStore);
  //// console.log(storeNames);

  const homeStoreInfo = storeInfoList?.filter((item) => item.id === homeStore);
  const timeEntriesEnabled = homeStoreInfo?.[0]?.time_entries_enabled;
  const trainingEnabled = homeStoreInfo?.[0]?.training_enabled;
  const engagementEnabled = homeStoreInfo?.[0]?.engagement_enabled;
  const breakWaiverEnabled = homeStoreInfo?.[0]?.break_waiver_enabled;
  const chatEnabled = homeStoreInfo?.[0]?.chat_enabled;
  const homeStoreTimezone = homeStoreInfo?.[0]?.timezone;
  const storeTimezones = React.useMemo(
    () =>
      Object.fromEntries(
        (storeInfoList || []).map((store) => [store.id?.toString(), store.timezone])
      ),
    [storeInfoList]
  );
  const employeeCreateChatEnabled =
    homeStoreInfo?.[0]?.employee_create_chat_enabled;
  const managerCreateChatEnabled =
    homeStoreInfo?.[0]?.manager_create_chat_enabled;
  const laborPercentEnabled = homeStoreInfo?.[0]?.labor_percent_enabled;
  const logbookEnabled = homeStoreInfo?.[0]?.logbook_enabled;
  const checkInsEnabled = homeStoreInfo?.[0]?.check_ins_enabled;
  const tplhEnabled = homeStoreInfo?.[0]?.tplh_enabled;
  const editShiftsInPastEnabled =
    homeStoreInfo?.[0]?.edit_shifts_in_past_enabled;
  const dailyPlanEnabled = homeStoreInfo?.[0]?.daily_plan_enabled;
  const approvalsEnabled = homeStoreInfo?.[0]?.approvals_enabled;
  const publishedScheduleEnabled = homeStoreInfo?.[0]?.published_schedule_enabled;
  const defaultBreakLengthHours =
    homeStoreInfo?.[0]?.default_break_length_hours;
  const paidBreakInputsEnabled = homeStoreInfo?.[0]?.paid_break_inputs_enabled;
  const businessDeclineShiftOptionEnabled =
    homeStoreInfo?.[0]?.business_decline_shift_option_enabled;
  const companyId = homeStoreInfo?.[0]?.company;
  const copilotEnabled = homeStoreInfo?.[0]?.copilot_enabled;
  const breakRequiredWhenShiftLengthExceeds =
    homeStoreInfo?.[0]?.break_required_when_shift_length_exceeds;
  const secondBreakShiftLengthThreshold =
    homeStoreInfo?.[0]?.second_break_shift_length_threshold;
  const proficiencyStarsCount =
    homeStoreInfo?.[0]?.company_info?.proficiency_stars_count;
  const proficiencyFromLMSEnabled =
    homeStoreInfo?.[0]?.proficiency_from_lms_enabled;
  const requestMinimumDaysInAdvance =
    homeStoreInfo?.[0]?.request_minimum_days_in_advance;
  const overviewEnabled = homeStoreInfo?.[0]?.overview_enabled;
  const payrollEnabled = homeStoreInfo?.[0]?.payroll_enabled;
  const payrollWagesEnabled = homeStoreInfo?.[0]?.payroll_wages_enabled;
  const wagesInRightworkEnabled =
    homeStoreInfo?.[0]?.wages_in_rightwork_enabled;
  const payrollEmployeeReviewEnabled =
    homeStoreInfo?.[0]?.payroll_employee_review_enabled;
  const documentsEnabled = homeStoreInfo?.[0]?.documents_enabled;
  const printHoursShownEnabled = homeStoreInfo?.[0]?.print_hours_shown_enabled;
  const deploymentEnabled = homeStoreInfo?.[0]?.deployment_enabled;
  const headcountPlanningEnabled =
    homeStoreInfo?.[0]?.headcount_planning_enabled;
  const timeEntriesRoleSelectionEnabled = homeStoreInfo?.[0]?.time_entries_role_selection_enabled;
  const timeEntriesHoursRoundedToTenths = homeStoreInfo?.[0]?.time_entries_hours_rounded_to_tenths_enabled
  const laborModelEnabled = homeStoreInfo?.[0]?.labor_model_enabled;
  const schedulingEnabled = homeStoreInfo?.[0]?.scheduling_enabled;
  const contestsEnabled = homeStoreInfo?.[0]?.contests_enabled;
  const assistantEnabled = homeStoreInfo?.[0]?.assistant_enabled;
  const operationsAssistantEnabled = homeStoreInfo?.[0]?.operations_assistant_enabled;
  const laborLawEnabled = homeStoreInfo?.[0]?.labor_law_enabled;
  const guestReviewsEnabled = homeStoreInfo?.[0]?.guest_reviews_enabled;
  const publishScheduleReviewRequiredEnabled =
    homeStoreInfo?.[0]?.publish_schedule_review_required_enabled;
  const blockPublishOnLaborViolationsEnabled =
    homeStoreInfo?.[0]?.block_publish_on_labor_violations_enabled;
  const payrollProvider = homeStoreInfo?.[0]?.payroll_provider;
  const tipCalculatorEnabled = homeStoreInfo?.[0]?.tip_calculator_enabled;
  const tipPoolingEnabled = Boolean(homeStoreInfo?.[0]?.tip_pooling_enabled);
  const blockManagerSelfTimecardEditEnabled =
    homeStoreInfo?.[0]?.block_manager_self_timecard_edit_enabled;
  const company = homeStoreInfo?.[0]?.company_info;
  const manualSalesInputEnabled = homeStoreInfo?.[0]?.manual_sales_input_enabled;
  const maintenanceEnabled = homeStoreInfo?.[0]?.maintenance_enabled;
  const itTicketsEnabled = homeStoreInfo?.[0]?.it_tickets_enabled;
  const marketingTicketsEnabled = homeStoreInfo?.[0]?.marketing_tickets_enabled;
  const menuTicketsEnabled = homeStoreInfo?.[0]?.menu_tickets_enabled;
  const boardsEnabled = homeStoreInfo?.[0]?.boards_enabled;
  const liveLaborEnabled = homeStoreInfo?.[0]?.live_labor_enabled;
  const reportingEnabled = homeStoreInfo?.[0]?.reporting_enabled;
  const aiReportingEnabled = homeStoreInfo?.[0]?.ai_reporting_enabled;
  const dashboardEnabled = homeStoreInfo?.[0]?.dashboard_enabled;
  const employeesEnabled = homeStoreInfo?.[0]?.employees_enabled;

  return (
    <div className="App">
    {isPublicSwipeDeck ? (
      <PublicSwipeDeckView token={publicSwipeDeckToken} />
    ) : isPublicForm ? (
      <PublicFormView token={publicFormToken} />
    ) : isAssetSticker ? (
      <AssetStickerPage />
    ) : isToastCredentialConnect ? (
      <ToastCredentialConnect token={toastCredentialToken} />
    ) : isWelcomeAccept ? (
      <WelcomeAccept token={welcomeToken} />
    ) : props.isAuthenticated && !isPasswordReset ? (
      isLoading ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <LoadSpinner />
          </div>
        ) : (
          <StreamChatWrapper
            homeStore={homeStore}
            username={props.username}
            isAuthenticated={props.isAuthenticated}
          >
            <MainDisplay
              storeOptions={storeOptions}
              homeStore={homeStore}
              homeCompany={companyId ?? homeCompany}
              company={company}
              storeNames={storeNames}
              change_home_store={change_home_store}
              username={props.username}
              refreshMemberStores={() =>
                get_member_stores(props.username, { showLoading: false })
              }
              permissionRole={permissionRole}
              currentUserId={currentUserId}
              permissionOverrides={permissionOverrides}
              timeEntriesEnabled={timeEntriesEnabled}
              trainingEnabled={trainingEnabled}
              engagementEnabled={engagementEnabled}
              chatEnabled={chatEnabled}
              homeStoreTimezone={homeStoreTimezone}
              storeTimezones={storeTimezones}
              breakWaiverEnabled={breakWaiverEnabled}
              laborPercentEnabled={laborPercentEnabled}
              logbookEnabled={logbookEnabled}
              checkInsEnabled={checkInsEnabled}
              defaultBreakLengthHours={defaultBreakLengthHours}
              tplhEnabled={tplhEnabled}
              managerCreateChatEnabled={managerCreateChatEnabled}
              employeeCreateChatEnabled={employeeCreateChatEnabled}
              editShiftsInPastEnabled={editShiftsInPastEnabled}
              dailyPlanEnabled={dailyPlanEnabled}
              approvalsEnabled={approvalsEnabled}
              publishedScheduleEnabled={publishedScheduleEnabled}
              copilotEnabled={copilotEnabled}
              breakRequiredWhenShiftLengthExceeds={
                breakRequiredWhenShiftLengthExceeds
              }
              secondBreakShiftLengthThreshold={
                secondBreakShiftLengthThreshold
              }
              proficiencyStarsCount={proficiencyStarsCount}
              proficiencyFromLMSEnabled={proficiencyFromLMSEnabled}
              requestMinimumDaysInAdvance={requestMinimumDaysInAdvance}
              overviewEnabled={overviewEnabled}
              payrollEnabled={payrollEnabled}
              payrollWagesEnabled={payrollWagesEnabled}
              payrollEmployeeReviewEnabled={payrollEmployeeReviewEnabled}
              documentsEnabled={documentsEnabled}
              printHoursShownEnabled={printHoursShownEnabled}
              deploymentEnabled={deploymentEnabled}
              headcountPlanningEnabled={headcountPlanningEnabled}
              timeEntriesRoleSelectionEnabled={timeEntriesRoleSelectionEnabled}
              timeEntriesHoursRoundedToTenths={timeEntriesHoursRoundedToTenths}
              laborModelEnabled={laborModelEnabled}
              schedulingEnabled={schedulingEnabled}
              contestsEnabled={contestsEnabled}
              assistantEnabled={assistantEnabled}
              operationsAssistantEnabled={operationsAssistantEnabled}
              setupAssistantEnabled={setupAssistantEnabled}
              laborLawEnabled={laborLawEnabled}
              guestReviewsEnabled={guestReviewsEnabled}
              paidBreakInputsEnabled={paidBreakInputsEnabled}
              businessDeclineShiftOptionEnabled={businessDeclineShiftOptionEnabled}
              publishScheduleReviewRequiredEnabled={publishScheduleReviewRequiredEnabled}
              blockPublishOnLaborViolationsEnabled={blockPublishOnLaborViolationsEnabled}
              wagesInRightworkEnabled={wagesInRightworkEnabled}
              payrollProvider={payrollProvider}
              tipCalculatorEnabled={tipCalculatorEnabled}
              tipPoolingEnabled={tipPoolingEnabled}
              blockManagerSelfTimecardEditEnabled={blockManagerSelfTimecardEditEnabled}
              manualSalesInputEnabled={manualSalesInputEnabled}
              maintenanceEnabled={maintenanceEnabled}
              itTicketsEnabled={itTicketsEnabled}
              marketingTicketsEnabled={marketingTicketsEnabled}
              menuTicketsEnabled={menuTicketsEnabled}
              boardsEnabled={boardsEnabled}
              liveLaborEnabled={liveLaborEnabled}
              reportingEnabled={reportingEnabled}
              aiReportingEnabled={aiReportingEnabled}
              dashboardEnabled={dashboardEnabled}
              employeesEnabled={employeesEnabled}
              internalBillingOverviewAccess={internalBillingOverviewAccess}
            />
          </StreamChatWrapper>
        )
      ) : (
        <Login uid={uid} token={token} />
      )}
      <ToastContainer position="bottom-left" newestOnTop autoClose={3000} />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: state.token !== null,
    username: state.user,
    email: state.email,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onTryAutoSignup: () => dispatch(actions.authCheckState()),
    logout: () => dispatch(actions.logout()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
